import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { novaFirmaInlineSchema } from "./hca-firmy";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro `hca.klienti` tRPC router.
 *
 * POZN.: Tato schémata se v S1 překrývala s `hca-lektori` v názvech
 * (`lektorIdSchema`, `createLektorSchema` atp.), ale zde jde o zcela
 * jiný doménový objekt (kontakt v CRM). Prefixy `klient*` zaručují
 * jedinečnost v re-exportu.
 */

export const kontaktTypEnum = z.enum([
  "UCASTNIK",
  "KLIENT",
  "PARTNER",
  "LEKTOR_EXT",
  "DODAVATEL",
  "JINY",
]);
export type KontaktTyp = z.infer<typeof kontaktTypEnum>;

/**
 * Base objekt kontaktu — bez refine, aby z něj šel odvodit `updateKlientSchema`
 * přes `.omit().partial()` (ZodEffects `.partial()` neumí).
 */
const createKlientBaseSchema = z.object({
  jmeno: z.string().min(1, "Jméno je povinné").max(255, "Jméno musí být 255 znaků nebo méně"),
  prijmeni: z.string().max(255, "Příjmení musí být 255 znaků nebo méně").optional(),
  email: z
    .string()
    .email("Neplatný formát e-mailu")
    .max(255, "E-mail musí být 255 znaků nebo méně")
    .toLowerCase()
    .optional(),
  telefon: z.string().max(40, "Telefon musí být 40 znaků nebo méně").optional(),
  typ: kontaktTypEnum.default("UCASTNIK"),
  firmaId: optionalUuid("Neplatné ID firmy"),
  /** Nová firma zadaná přímo ve formuláři kontaktu (dedupe řeší server). */
  novaFirma: novaFirmaInlineSchema.optional(),
  pozice: z.string().max(255, "Pozice musí být 255 znaků nebo méně").optional(),
  obchodnikId: z.string().max(255, "ID obchodníka musí být 255 znaků nebo méně").optional(),
  poznamka: z.string().max(2000, "Poznámka musí být 2000 znaků nebo méně").optional(),
  zdroj: z.string().max(100, "Zdroj musí být 100 znaků nebo méně").optional(),
});

/** Vytvoření kontaktu. Firma buď existující (`firmaId`), nebo nová — ne obojí. */
export const createKlientSchema = createKlientBaseSchema.refine(
  (data) => !(data.firmaId && data.novaFirma),
  {
    message: "Zadejte buď existující firmu, nebo novou — ne obojí",
    path: ["novaFirma"],
  },
);

/**
 * Partial update + povinné id. `firmaId` je navíc nullable — odeslání `null`
 * znamená "odpojit firmu", `undefined` znamená "neměnit". Bez nullable by
 * editační formulář nedokázal vazbu na firmu zrušit. `novaFirma` v update
 * není — editace mění jen vazbu, firmy se zakládají v create/quickAdd flow.
 */
export const updateKlientSchema = createKlientBaseSchema
  .omit({ novaFirma: true })
  .partial()
  .extend({
    id: z.string().uuid("Neplatné ID klienta"),
    firmaId: z.string().uuid("Neplatné ID firmy").nullable().optional(),
  });

/** E-mail health filter — rychlé třídění podle deliverability stavu. */
export const klientEmailHealthEnum = z.enum(["all", "clean", "unsubscribed", "bounced"]);
export type KlientEmailHealth = z.infer<typeof klientEmailHealthEnum>;

/** Whitelist řazení kontaktů — router mapuje klíče na sloupce. */
export const KLIENTI_SORT_KEYS = ["prijmeni", "firma", "email", "vytvoreno"] as const;
export type KlientiSortKey = (typeof KLIENTI_SORT_KEYS)[number];

/** List kontaktů. */
export const listKlientiSchema = paginationSchema.merge(sortSchema(KLIENTI_SORT_KEYS)).extend({
  typ: kontaktTypEnum.optional(),
  firmaId: optionalUuid(),
  obchodnikId: z.string().max(255, "ID obchodníka musí být 255 znaků nebo méně").optional(),
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
  archivovane: z.coerce.boolean().optional(),
  emailHealth: klientEmailHealthEnum.default("all"),
  /** Filter: klienti s tímto štítkem (junction lookup). */
  tagId: optionalUuid(),
  /**
   * Režim pro našeptávač: přeskočí `count(*)` přes celou tabulku, který
   * napovídání jen zdržuje. Odpověď pak má `total: -1` — kdo potřebuje
   * stránkování, flag neposílá.
   */
  lightweight: z.boolean().optional(),
});

/** ID-only schema. */
export const klientIdSchema = z.object({
  id: z.string().uuid("Neplatné ID klienta"),
});

/**
 * Kontrola možné duplicity při zakládání kontaktu. Všechna kritéria jsou
 * volitelná — server hledá shodu podle e-mailu (exact, case-insensitive),
 * telefonu (jen číslice, posledních 9) a příjmení (case-insensitive).
 * Bez `.email()` — volá se na blur i nad rozepsanou adresou a nevalidní
 * hodnota prostě nic nenajde, nemá shodit dotaz validační chybou.
 */
export const checkKlientDuplicateSchema = z.object({
  email: z.string().trim().toLowerCase().max(255, "E-mail musí být 255 znaků nebo méně").optional(),
  telefon: z.string().trim().max(40, "Telefon musí být 40 znaků nebo méně").optional(),
  prijmeni: z.string().trim().max(255, "Příjmení musí být 255 znaků nebo méně").optional(),
});

export type CheckKlientDuplicateInput = z.infer<typeof checkKlientDuplicateSchema>;

/** Lookup firmy v ARES dle IČO. Vstup pro `klienti.aresLookup`. */
export const aresLookupSchema = z.object({
  ico: z
    .string()
    .max(8, "IČO musí být 8 znaků")
    .regex(/^\d{8}$/, "IČO musí být přesně 8 číslic"),
});

/** Přiřazení obchodníka. */
export const assignObchodnikSchema = z.object({
  klientId: z.string().uuid("Neplatné ID klienta"),
  obchodnikId: z.string().max(255, "ID obchodníka musí být 255 znaků nebo méně").nullable(),
});

/** Public odhlášení z marketingové komunikace — token z mailu. */
export const unsubscribeSchema = z.object({
  token: z
    .string()
    .min(8, "Neplatný odhlašovací token")
    .max(128, "Odhlašovací token musí být 128 znaků nebo méně"),
});

/** Admin — vymazání bounce flagu klienta po opravě inbox/spam filter. */
export const clearKlientBounceSchema = z.object({
  id: z.string().uuid("Neplatné ID klienta"),
});

/** Admin — manuální override unsubscribe stavu (bez klient tokenu). */
export const setKlientUnsubscribedSchema = z.object({
  id: z.string().uuid("Neplatné ID klienta"),
  unsubscribed: z.boolean(),
});

/**
 * Hromadný import kontaktů (CSV z Raynetu / tabulky). Řádky parsuje klient
 * (`parseCsv`), server dedupuje podle e-mailu a firmy dohledá/založí dle názvu.
 */
export const bulkImportKlientiSchema = z.object({
  rows: z
    .array(
      z.object({
        jmeno: z.string().trim().min(1, "Jméno je povinné").max(255),
        prijmeni: z.string().trim().max(255).optional(),
        // Bez .email() — vadnou adresu router zahodí (kontakt vznikne bez ní),
        // jeden špatný řádek nesmí shodit celou dávku importu.
        email: z.string().trim().toLowerCase().max(255).optional(),
        telefon: z.string().trim().max(32).optional(),
        firmaNazev: z.string().trim().max(255).optional(),
      }),
    )
    .min(1, "Import je prázdný")
    .max(1000, "Maximálně 1000 kontaktů najednou"),
});

export type BulkImportKlientiInput = z.infer<typeof bulkImportKlientiSchema>;

export type CreateKlientInput = z.infer<typeof createKlientSchema>;
export type UpdateKlientInput = z.infer<typeof updateKlientSchema>;
export type ListKlientiInput = z.infer<typeof listKlientiSchema>;
export type KlientIdInput = z.infer<typeof klientIdSchema>;
export type AresLookupInput = z.infer<typeof aresLookupSchema>;
export type AssignObchodnikInput = z.infer<typeof assignObchodnikSchema>;
export type UnsubscribeInput = z.infer<typeof unsubscribeSchema>;
export type ClearKlientBounceInput = z.infer<typeof clearKlientBounceSchema>;
export type SetKlientUnsubscribedInput = z.infer<typeof setKlientUnsubscribedSchema>;
